import { DownloadIcon } from "assets/icons";
import { If } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { t } from "i18next";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { useOrdersAndReturnsListRealTimeUpdate } from "modules/orders";
import { QRCodeCanvas } from "qrcode.react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Pagination, SearchInput } from "ui";

import { useReturnsDetailsByIds } from "../../apis";
import { ReturnsTable, ReturnModal } from "../../components";
import { downloadReturnPdf } from "../../helpers";
import { useReturnsList } from "../../hooks";

import styles from "./Returns.module.scss";

const Returns = () => {
  const {
    returns,
    refetchReturns,
    searchValue,
    setSearchValue,
    isLoading,
    sorting,
    page,
    setPage,
    totalItems,
    setSorting
  } = useReturnsList();

  const [selectedReturnIds, setSelectedReturnIds] = useState<string[]>([]);

  const navigate = useNavigate();

  const {
    mutateAsync: getReturnsDetailsByIds,
    isLoading: isReturnsForDownloadLoading
  } = useReturnsDetailsByIds();

  const [searchParams] = useSearchParams();
  const openedReturn = searchParams.get("returnId");

  useOrdersAndReturnsListRealTimeUpdate();

  return (
    <ContentLayout
      title={t("Returns")}
      action={
        selectedReturnIds.length !== 0 && (
          <>
            <Button
              type="button"
              className={styles.downloadButton}
              title={`${t("Download QR & Details Sheet")} (${selectedReturnIds.length})`}
              icon={<DownloadIcon width={24} height={24} />}
              variant="default"
              loading={isReturnsForDownloadLoading}
              onClick={async () => {
                const orderDetailsForDownload =
                  await getReturnsDetailsByIds(selectedReturnIds);
                downloadReturnPdf(orderDetailsForDownload);
              }}
            />

            {selectedReturnIds.map((orderId) => (
              <QRCodeCanvas
                key={orderId}
                value={orderId}
                id={`qr-${orderId}`}
                style={{ display: "none" }}
              />
            ))}
          </>
        )
      }
    >
      <BoxShadowLayout className={styles.pageContent}>
        <div className={styles.pageContent_tableHeaderControls}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={`${t("Search")}...`}
          />
        </div>

        <ReturnsTable
          returnsList={returns ?? []}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          refetchReturns={refetchReturns}
          selectedReturnsIds={selectedReturnIds}
          setSelectedReturnsIds={setSelectedReturnIds}
        />

        <Pagination
          currentPage={page}
          onChange={setPage}
          perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
          totalItems={totalItems}
        />
      </BoxShadowLayout>

      <If condition={!!openedReturn}>
        <ReturnModal
          returnItemId={openedReturn as string}
          refetchReturns={refetchReturns}
          isOpen={!!openedReturn}
          onClose={() => navigate({ search: "" })}
        />
      </If>
    </ContentLayout>
  );
};

export default Returns;
